<section class="surface-card p-4 sm:p-6">
  <div class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h2 class="mb-1 text-xl font-semibold text-text-primary">
        {{ 'seekerDashboard.myProperties.title' | translate }}
      </h2>
      <p class="text-sm text-text-secondary">
        {{ 'seekerDashboard.myProperties.subtitle' | translate }}
      </p>
    </div>
    <div class="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-surface-elevated text-primary">
      <i class="fa-solid fa-house-chimney text-xl"></i>
    </div>
  </div>

  @if (loading) {
    <div class="rounded-xl border border-border bg-surface shadow-elevated">
      <div class="border-b border-border-strong bg-surface-elevated px-4 py-4">
        <div class="flex items-center gap-3">
          <span class="loader"></span>
          <span class="text-sm font-medium text-text-secondary">{{ 'seekerDashboard.myProperties.loading' | translate }}</span>
        </div>
      </div>
      <div class="grid gap-4 p-4 md:grid-cols-2">
        @for (row of [1, 2, 3, 4]; track row) {
          <div class="rounded-xl border border-border-subtle bg-surface-elevated p-4">
            <div class="skeleton mb-4 aspect-[16/9] w-full"></div>
            <div class="skeleton mb-2 h-4 w-3/4"></div>
            <div class="skeleton h-3 w-1/2"></div>
          </div>
        }
      </div>
    </div>
  } @else if (error) {
    <div class="alert alert-danger mx-auto max-w-xl rounded-xl shadow-elevated">
      <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
      <div>
        <p class="font-semibold text-text-primary">{{ 'seekerDashboard.myProperties.errorTitle' | translate }}</p>
        <p class="mt-1 text-sm text-text-secondary">{{ error }}</p>
        <button type="button" class="btn btn-danger mt-4" (click)="loadProperties()">
          <i class="fa-solid fa-rotate-right text-xs"></i>
          {{ 'seekerDashboard.myProperties.tryAgain' | translate }}
        </button>
      </div>
    </div>
  } @else {
    <div class="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
      <article class="rounded-xl border border-border-strong bg-surface-elevated p-4 shadow-subtle">
        <p class="mb-1 text-xs font-medium uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.totalPortfolio' | translate }}</p>
        <p class="text-2xl font-bold text-text-primary">{{ totalProperties }}</p>
      </article>
      <article class="rounded-xl border border-success/20 bg-success-soft p-4 shadow-subtle">
        <p class="mb-1 text-xs font-medium uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.totalOwned' | translate }}</p>
        <p class="text-2xl font-bold text-success">{{ summary.totalOwned }}</p>
      </article>
      <article class="rounded-xl border border-border-primary bg-primary-soft p-4 shadow-subtle">
        <p class="mb-1 text-xs font-medium uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.totalRented' | translate }}</p>
        <p class="text-2xl font-bold text-primary">{{ summary.totalRented }}</p>
      </article>
    </div>

    @if (owned.length === 0 && rented.length === 0) {
      <div class="rounded-xl border border-border bg-surface px-4 py-16 text-center shadow-elevated">
        <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-strong bg-surface-elevated text-text-muted">
          <i class="fa-regular fa-folder-open text-xl"></i>
        </div>
        <h3 class="text-base font-semibold text-text-primary">{{ 'seekerDashboard.myProperties.emptyTitle' | translate }}</h3>
        <p class="mx-auto mt-2 max-w-sm text-sm text-text-secondary">{{ 'seekerDashboard.myProperties.emptyDescription' | translate }}</p>
      </div>
    } @else {
      <div class="space-y-8">
        <div>
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3 class="flex items-center gap-2 text-lg font-semibold text-text-primary">
              <i class="fa-solid fa-key text-primary"></i>
              {{ 'seekerDashboard.myProperties.ownedTitle' | translate }}
            </h3>
            <span class="badge badge-success">{{ owned.length }}</span>
          </div>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            @for (item of owned; track item.dealId) {
              <article class="group rounded-xl border border-border-strong bg-surface p-5 shadow-subtle transition-all duration-300 hover:-translate-y-0.5 hover:border-success/30 hover:bg-surface-hover hover:shadow-elevated">
                <div class="mb-5 flex items-start justify-between gap-4 border-b border-border-subtle pb-4">
                  <div class="flex min-w-0 items-start gap-3">
                    <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-success/20 bg-success-soft text-success">
                      <i class="fa-solid fa-file-invoice-dollar"></i>
                    </div>
                    <div class="min-w-0">
                      <h4 class="truncate text-base font-semibold text-text-primary">{{ 'seekerDashboard.myProperties.ownedLabel' | translate }}</h4>
                      <p class="mt-1 font-mono text-xs text-text-muted" dir="ltr">{{ item.dealId }}</p>
                    </div>
                  </div>
                  <span class="badge whitespace-nowrap" [ngClass]="statusClass(item.status)">
                    {{ translationValue(item.status, 'seekerDashboard.myProperties.status') | translate }}
                  </span>
                </div>

                <div class="grid grid-cols-2 gap-3 text-sm">
                  <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                    <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.finalPrice' | translate }}</p>
                    <p class="font-bold text-success">{{ item.finalPrice | number }} <span class="text-xs text-text-muted">{{ item.currency }}</span></p>
                  </div>
                  <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                    <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.paymentMethod' | translate }}</p>
                    <p class="font-semibold text-text-primary capitalize">{{ translationValue(item.paymentMethod, 'seekerDashboard.myProperties.paymentMethodValue') | translate }}</p>
                  </div>
                  <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                    <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.purchaseDate' | translate }}</p>
                    <p class="font-medium text-text-primary">{{ formatDate(item.purchaseDate) }}</p>
                  </div>
                  <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                    <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.targetType' | translate }}</p>
                    <p class="font-semibold text-text-primary capitalize">{{ item.targetType }}</p>
                  </div>
                </div>
              </article>
            } @empty {
              <div class="rounded-xl border border-border bg-surface px-4 py-10 text-center lg:col-span-2">
                <p class="text-sm text-text-secondary">{{ 'seekerDashboard.myProperties.noOwned' | translate }}</p>
              </div>
            }
          </div>
        </div>

        <div>
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3 class="flex items-center gap-2 text-lg font-semibold text-text-primary">
              <i class="fa-solid fa-calendar-check text-primary"></i>
              {{ 'seekerDashboard.myProperties.rentedTitle' | translate }}
            </h3>
            <span class="badge badge-primary">{{ rented.length }}</span>
          </div>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            @for (item of rented; track item.contractId) {
              <article class="group overflow-hidden rounded-xl border border-border-strong bg-surface shadow-subtle transition-all duration-300 hover:-translate-y-0.5 hover:border-border-primary hover:bg-surface-hover hover:shadow-elevated">
                <div class="grid sm:grid-cols-[168px_1fr]">
                  <div class="relative min-h-44 bg-surface-elevated">
                    @if (unitImage(item.unit); as imageUrl) {
                      <img [src]="imageUrl" [alt]="unitTitle(item.unit)" class="h-full min-h-44 w-full object-cover">
                    } @else {
                      <div class="flex h-full min-h-44 items-center justify-center text-text-muted">
                        <i class="fa-regular fa-image text-3xl"></i>
                      </div>
                    }
                    <div class="absolute left-3 top-3 rtl:left-auto rtl:right-3">
                      <span class="badge" [ngClass]="statusClass(item.status)">
                        {{ translationValue(item.status, 'seekerDashboard.myProperties.status') | translate }}
                      </span>
                    </div>
                  </div>

                  <div class="p-5">
                    <div class="mb-4 min-w-0">
                      <h4 class="truncate text-base font-semibold text-text-primary" dir="auto">
                        @if (isTranslationKey(unitTitle(item.unit))) {
                          {{ unitTitle(item.unit) | translate }}
                        } @else {
                          {{ unitTitle(item.unit) }}
                        }
                      </h4>
                      <p class="mt-2 flex items-start gap-2 text-sm leading-6 text-text-secondary" dir="auto">
                        <i class="fa-solid fa-location-dot mt-1 text-text-muted"></i>
                        <span>
                          @if (isTranslationKey(unitLocation(item.unit))) {
                            {{ unitLocation(item.unit) | translate }}
                          } @else {
                            {{ unitLocation(item.unit) }}
                          }
                        </span>
                      </p>
                    </div>

                    <div class="grid grid-cols-2 gap-3 text-sm">
                      <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                        <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.rentAmount' | translate }}</p>
                        <p class="font-bold text-primary">{{ item.rentAmount | number }} <span class="text-xs text-text-muted">EGP</span></p>
                      </div>
                      <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                        <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.paymentFrequency' | translate }}</p>
                        <p class="font-semibold text-text-primary capitalize">{{ translationValue(item.paymentFrequency, 'seekerDashboard.myProperties.paymentFrequencyValue') | translate }}</p>
                      </div>
                      <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                        <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.startDate' | translate }}</p>
                        <p class="font-medium text-text-primary">{{ formatDate(item.startDate) }}</p>
                      </div>
                      <div class="rounded-lg border border-border-subtle bg-surface-elevated p-3">
                        <p class="mb-1 text-xs uppercase tracking-wider text-text-muted">{{ 'seekerDashboard.myProperties.endDate' | translate }}</p>
                        <p class="font-medium text-text-primary">{{ formatDate(item.endDate) }}</p>
                      </div>
                    </div>

                    <div class="mt-4 border-t border-border-subtle pt-3">
                      <p class="flex items-center gap-2 font-mono text-xs text-text-muted" dir="ltr">
                        <i class="fa-solid fa-fingerprint"></i>
                        {{ item.contractId }}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            } @empty {
              <div class="rounded-xl border border-border bg-surface px-4 py-10 text-center lg:col-span-2">
                <p class="text-sm text-text-secondary">{{ 'seekerDashboard.myProperties.noRented' | translate }}</p>
              </div>
            }
          </div>
        </div>
      </div>
    }
  }
</section>
